import { ReactComponent as ArrowIcon } from '@apps/gated-2021/assets/arrow-right-one.svg';
import { ReactComponent as MapPinIcon } from '@apps/gated-2021/assets/map-pin.svg';
import { GooglePlace } from '@codecs/google';
import { useOnClickOutside } from '@hooks/useOnClickOutside';
import { getAddressSuggestions } from '@lib/api';
import { normalizeAddress } from '@lib/format';
import { debounce } from 'lodash';
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import * as styled from './GatedAddressInput.styled';

const debounceMs = 300;

export const GatedAddressInput: FunctionComponent<{
    defaultAddress?: string;
    autoFocus?: boolean;
    className?: string;
    onSubmit: (address: string, placeID: string) => void;
}> = ({ defaultAddress, autoFocus, className, onSubmit }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [value, setValue] = useState<string | undefined>(defaultAddress);
    const [placeId, setPlaceId] = useState<string | undefined>();
    const [placeIndex, setPlaceIndex] = useState<number | undefined>();
    const [places, setPlaces] = useState<GooglePlace[]>([]);

    /** Whether displayed address is a canonical autocomplete result */
    let isCanonical = defaultAddress !== undefined;

    const ref = React.createRef<HTMLInputElement>();

    /** Place cursor in input box */
    const focus = () => {
        if (ref.current) ref.current.focus();
    };

    useEffect(focus, []);

    const resetPlaces = (newPlaces: GooglePlace[] = []) => {
        setIsLoading(false);
        setPlaceIndex(undefined);
        setPlaces(newPlaces);
        focus();
    };

    const dropdownRef = useRef(null);
    useOnClickOutside(dropdownRef, () => {
        setPlaceIndex(undefined);
        resetPlaces();
    });

    /** Submit suggestion as user's address */
    const submitSuggestion = (place: GooglePlace) => {
        const address = normalizeAddress(place.description);

        resetPlaces();
        setValue(address);
        setPlaceId(place.placeID);
        onSubmit(address, place.placeID);
    };

    const debounceSuggestions = useCallback(
        debounce(async (search: string) => {
            setIsLoading(true);
            if (search) {
                getAddressSuggestions(search).then(resetPlaces);
            } else {
                resetPlaces();
            }
        }, debounceMs),
        [],
    );

    useEffect(() => {
        return () => {
            debounceSuggestions.cancel();
        };
    }, []);

    const onButtonClick = () => {
        if (value && placeId) {
            onSubmit(value, placeId);
        }
    };

    /** User clicked or pressed `Enter` on a suggested address */
    const onSelectPlace = (e: React.MouseEvent | null, index: number) => {
        if (e) e.preventDefault();
        setPlaceIndex(index);
        submitSuggestion(places[index]);
        focus();
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                moveSelection(-1);
                break;
            case 'ArrowDown':
                moveSelection(1);
                break;
            case 'Enter':
                if (placeIndex !== undefined) {
                    // use the currently selected suggestion
                    const place = places[placeIndex];
                    if (place) submitSuggestion(place);
                } else if (places.length > 0) {
                    // assume user wants to use first suggestion
                    onSelectPlace(null, 0);
                }
                break;
            case 'Escape':
                setPlaceIndex(undefined);
                resetPlaces();
                break;
            default:
                // handle reset in keyDown instead of onChange event since it
                // isn't debounced in keyDown
                if (isCanonical) {
                    isCanonical = false;
                    resetPlaces();
                }
                break;
        }
    };

    /** Move suggestion selection in given direction */
    const moveSelection = (dir: 1 | -1) => {
        if (isLoading || places.length === 0) return;

        let newIndex: number | undefined;
        const lastIndex = places.length - 1;

        if (placeIndex !== undefined) {
            newIndex = placeIndex + dir;
            if (newIndex > lastIndex || newIndex < 0) newIndex = undefined;
        } else {
            newIndex = dir < 0 ? lastIndex : 0;
        }
        setPlaceIndex(newIndex);
    };

    const hasResult = places.length > 0;
    const renderSuggestions = () => (
        <>
            {hasResult && (
                <styled.Suggestions ref={dropdownRef}>
                    <ul data-cy="address-suggestions">
                        {places.map((p, i) => (
                            <PlaceItem
                                key={p.placeID}
                                place={p}
                                index={i}
                                selectedIndex={placeIndex}
                                onClick={(e) => onSelectPlace(e, i)}
                            />
                        ))}
                    </ul>
                </styled.Suggestions>
            )}
        </>
    );

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (event) => event.target.select();

    return (
        <styled.AddressInput className={className}>
            <styled.Search hasResult={hasResult}>
                <MapPinIcon />
                <input
                    data-cy="input-address"
                    defaultValue={defaultAddress}
                    value={value}
                    autoFocus={autoFocus}
                    onFocus={handleFocus}
                    autoComplete="off"
                    onChange={(e) => {
                        setValue(e.target.value);
                        debounceSuggestions(e.target.value);
                    }}
                    onKeyDown={onKeyDown}
                    placeholder="Vacation home address"
                    aria-label="Vacation home address"
                    type="text"
                    ref={ref}
                />
                {renderSuggestions()}
            </styled.Search>
            <styled.SubmitButton onClick={onButtonClick}>
                <ArrowIcon />
            </styled.SubmitButton>
        </styled.AddressInput>
    );
};

/** Single address (place) suggestion */
const PlaceItem: FunctionComponent<{
    key: string;
    place: GooglePlace;
    index: number;
    selectedIndex?: number;
    onClick: (e: React.MouseEvent) => void;
}> = ({ place, index, selectedIndex, onClick }) => (
    <li>
        <styled.Suggestion
            selected={index === selectedIndex}
            id={`address-suggestion-${place.placeID}`}
        >
            <a href={'#'} onClick={onClick} key={place.placeID} tabIndex={0}>
                <MapPinIcon />
                <span>{place.description}</span>
            </a>
        </styled.Suggestion>
    </li>
);
